import { format } from "date-fns";

import { osloDateKey } from "../misc/osloDate";
import type { Deal, DealStage } from "../types";
import { isDealOpen } from "./dealStatus";

export const findDealLabel = (
  dealStages: DealStage[],
  dealValue: string | null | undefined,
) => {
  if (!dealValue) return undefined;
  const dealStage = dealStages.find((stage) => stage.value === dealValue);
  return dealStage?.label;
};

// Stadiet for en åpen mulighet, ellers statusen (Vunnet, Tapt,
// Diskvalifisert). Brukes der muligheter i alle statuser listes sammen.
export const dealStageOrStatusLabel = (
  deal: Pick<Deal, "stage" | "status">,
  dealStages: DealStage[],
  translate: (key: string, options?: Record<string, unknown>) => string,
) =>
  isDealOpen(deal)
    ? (findDealLabel(dealStages, deal.stage) ?? deal.stage ?? "")
    : translate(`crm.deals.status.${deal.status}`, { _: deal.status });

// Dagens dato i Europe/Oslo (PRD US12.3): «forfalt» og «i dag» skal bety det
// samme for alle, også rundt midnatt og med en nettleser i en annen tidssone.
export const todayKey = () => osloDateKey();

// Forfalt neste steg (PR-6): datoen er passert og muligheten er fortsatt åpen.
export const isNextStepOverdue = (
  deal: Pick<Deal, "next_step_date" | "status">,
): boolean =>
  deal.next_step_date != null &&
  deal.next_step_date < todayKey() &&
  isDealOpen(deal);

export function getRelativeTimeString(
  dateString: string,
  locale = "en",
): string {
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diff = date.getTime() - today.getTime();
  const unitDiff = Math.round(diff / (1000 * 60 * 60 * 24));

  // Check if the date is more than one week old
  if (Math.abs(unitDiff) > 7) {
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
    }).format(date);
  }

  // Intl.RelativeTimeFormat for dates within the last week
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  return ucFirst(rtf.format(unitDiff, "day"));
}

function ucFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const isoDateStringRegex = /^\d{4}-\d{2}-\d{2}$/;

export function formatISODateString(dateString: string) {
  if (!isoDateStringRegex.test(dateString)) {
    throw new Error("Invalid date format. Expected YYYY-MM-DD.");
  }
  // Some browsers will consider a date in the format YYYY-MM-DD as UTC, which can cause off-by-one-day issues depending on the user's timezone.
  // To avoid this, we can parse the date components manually and create a date object in the local timezone.
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  // Norsk kortformat, som datoene på notatene i samme dialog («24.9.2026»).
  // «PP» uten locale ga engelsk «Jun 15, 2024».
  return format(date, "d.M.yyyy");
}
